import React, { useMemo } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';

const DueDatesSummary = ({ project }) => {
  // Extract all due dates from project, phases, and steps
  const upcomingDueDates = useMemo(() => {
    if (!project) return [];

    const dueDates = [];
    
    // Add project due date if it exists
    if (project.due_date) {
      dueDates.push({
        id: `project-${project.id}`,
        name: project.name,
        type: 'Project',
        date: new Date(project.due_date),
        color: '#3b82f6' // blue-500
      });
    }
    
    // Add phase due dates
    if (project.phases && project.phases.length > 0) {
      project.phases.forEach(phase => {
        if (phase.due_date) {
          dueDates.push({
            id: `phase-${phase.id}`,
            name: phase.name,
            type: 'Phase',
            date: new Date(phase.due_date),
            color: '#4b5563' // gray-600
          });
        }
        
        // Add step due dates
        if (phase.steps && phase.steps.length > 0) {
          phase.steps.forEach(step => {
            if (step.due_date) {
              dueDates.push({
                id: `step-${step.id}`,
                name: step.name,
                phaseId: phase.id,
                phaseName: phase.name,
                type: 'Step',
                date: new Date(step.due_date),
                color: '#6b7280' // gray-500
              });
            }
          });
        }
      });
    }
    
    // Filter out past due dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const upcomingDates = dueDates
      .filter(item => item.date >= today)
      .sort((a, b) => a.date - b.date);
    
    return upcomingDates.slice(0, 5); // Return only the next 5 upcoming due dates
  }, [project]);
  
  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Calculate days remaining
  const getDaysRemaining = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };
  
  // Get appropriate background color based on days remaining
  const getTimingColor = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 3) return 'bg-red-100 text-red-800'; // Urgent
    if (diffDays <= 7) return 'bg-yellow-100 text-yellow-800'; // Soon
    return 'bg-blue-100 text-blue-800'; // Normal
  };
  
  if (!upcomingDueDates || upcomingDueDates.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-blue-50 to-blue-100">
        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-blue-500" />
          Upcoming Due Dates
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Timeline of upcoming project, phase, and step deadlines.
        </p>
      </div>
      <div className="border-t border-gray-200">
        <ul className="divide-y divide-gray-200">
          {upcomingDueDates.map((item) => (
            <li key={item.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <span 
                      className="inline-flex w-2 h-2 mr-2 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    ></span>
                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  </div>
                  <div className="mt-1">
                    <p className="text-xs text-gray-500">
                      {item.type === 'Step' ? `${item.type} in ${item.phaseName}` : item.type}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <div className={`px-2 py-1 rounded-md text-xs font-medium ${getTimingColor(item.date)}`}>
                    {getDaysRemaining(item.date)}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {formatDate(item.date)}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DueDatesSummary; 